import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Button, ButtonGroup,
  FormController,
  FormLabel, Radio,
  RadioGroup,
  SelectionControlLabel,
  DrawerBody,
  DrawerFooter,
  Alert,
  TextField,
} from '@one-thd/sui-atomic-components';
import {
  extend, arrayOf, params, string, number, shape, customType, useLazyDataModel, bool
} from '@thd-nucleus/data-sources';
import { AddToListProductInfo } from './AddToListProductInfo';
import { BulkProductsSummary } from './BulkProductsSummary';
import { AddToCoreContext } from '../../core/AddToCoreContext';
import { TRACK, SCREENS, ERROR, SELECTION_TYPE } from '../../AddToConstants';
import { useCreateList } from '../../hooks/useLists';
import { useListMetrics } from '../../hooks/useListMetrics';
import { triggerAnalytics } from '../../AddToHelpers';
import { organizeItemsByGroup } from '../../../utils/copyLists.utils';

export const SelectList = ({
  listDetails,
  onClose,
  onCreate,
  isExistingLists,
  products,
  isSharedList,
  setFavoriteCount,
  favoriteCount,
  productValue,
  identifiers,
  primaryImage,
  setNewListDetails,
  selectedList,
  setSelectedList
}) => {
  const {
    setScreen, setSelectedListId, setSelectedListName, setShowSuccessMessage, sourceScreen
  } = useContext(AddToCoreContext);
  const [name, setName] = useState('');
  const [error, setError] = useState(null);

  const { LIST_SELECT_GROUP, LIST_SUCCESS } = SCREENS;

  // Set up lazy mutation for group-aware submission (Scenario 2 bypass)
  const [submitListOp, {
    data: listOpData,
    loading: listOpLoading,
  }] = useLazyDataModel('listOperations', {
    context: { withAuth: true },
    fetchPolicy: 'no-cache'
  });

  // Prepare items for useListMetrics - format products for pricing API
  const itemsForMetrics = products.map((product) => ({
    itemId: product.itemId,
    quantity: product.quantity || 1,
    price: product.price || 0 // Fallback price for items without pricing
  }));

  const { myListInformation } = useListMetrics(itemsForMetrics, {});
  const { uniqueSkuCount, quantityTotal, listTotal } = myListInformation;

  useEffect(() => {
    triggerAnalytics(TRACK.COPY_TO_SELECT_OVERLAY, { selectionType: SELECTION_TYPE.LIST });
  }, []);

  const isMultiAdd = products?.length > 1;
  const singleProduct = [
    { itemId: products[0]?.itemId, quantity: products[0]?.quantity }
  ];

  const multipleProduct = products.map((prod) => ({
    itemId: prod.itemId,
    quantity: prod.quantity
  }));

  const { createList, createListResponse } = useCreateList();

  const selectList = (list) => {
    setSelectedList({
      listId: list?.listId,
      listName: list?.listName
    });
    setSelectedListId(list?.listId);
    setSelectedListName(list?.listName);
    setName('');
    setError(null);
  };

  const getValidationError = (value) => {
    if (!value) {
      setError(null);
      return;
    }

    const validCharacters = /^[ [\]{}|a-z|A-Z|!@#$%^&*()0-9-_+,.?"':;\\]+$/;
    let isValid = validCharacters.test(value);
    if (!isValid) {
      setError(ERROR.INVALID_LIST_NAME);
    } else {
      setError(null);
    }
  };

  const submitWithGroups = async (targetListId) => {
    const { defaultGroups, namedGroups } = organizeItemsByGroup(products);
    const mutations = [];

    // AddItems for default/ungrouped items
    if (defaultGroups.length > 0) {
      mutations.push(
        submitListOp({
          variables: {
            operationName: 'AddItems',
            id: targetListId,
            groups: defaultGroups,
          }
        })
      );
    }

    // Create for named groups (backend matches by name or creates new groups)
    if (namedGroups.length > 0) {
      mutations.push(
        submitListOp({
          variables: {
            operationName: 'Create',
            id: targetListId,
            groups: namedGroups,
          }
        })
      );
    }

    await Promise.all(mutations);
  };

  // Handle success from group-aware submission
  useEffect(() => {
    if (listOpData?.listOperations?.id) {
      // 6.13.1 Add to List action (group-aware bypass)
      const location = isSharedList ? 'lists: shared list' : sourceScreen;
      const items = products.map((product) => ({
        quantity: product.quantity || 1,
        price: product?.price || 'N/A',
        sku: product.itemId,
        aiQuantityEstimate: product?.aiQuantityEstimate ? 'yes' : 'no'
      }));
      triggerAnalytics(TRACK.ADD_TO_LIST_ADD_ACTION, {
        name: 'list add',
        location,
        items,
        isBulkAction: products.length > 1,
        listTotal: myListInformation.listTotal || 0,
        uniqueSkuCount: myListInformation.uniqueSkuCount || 0,
        quantityTotal: myListInformation.quantityTotal || 0,

      });
      setShowSuccessMessage(true);
      setScreen(LIST_SUCCESS);
    }
  }, [listOpData]);

  const hasGroupInfo = products.some((p) => p.groupName);

  const handleNextAction = async (evt) => {
    evt.preventDefault();
    if (selectedList?.listId) {
      if (hasGroupInfo) {
        // Skip group selection — submit directly via listOperations
        await submitWithGroups(selectedList.listId);
        return;
      }
      setScreen(LIST_SELECT_GROUP);
    }

    if (name && !error) {
      await createList({
        variables: {
          name
        }
      });
    }
  };

  useEffect(() => {
    if (!createListResponse.error && createListResponse?.data?.createList?.name) {
      let creationLocation = isExistingLists ? 'choose a list overlay' : 'create a list overlay';
      if (!isMultiAdd && products[0]?.itemId && !isSharedList) {
        if (window.THDCustomer?.default?.customerType === 'B2C') {
          creationLocation = 'save to your list overlay';
        }

        // 6.10.2 New List Created w/Product Add - single item
        LIFE_CYCLE_EVENT_BUS.trigger(TRACK.NEW_LIST_CREATED_WITH_PRODUCT_ADD, {
          location: creationLocation,
          items: [{
            quantity: products[0]?.quantity || 1,
            price: products[0]?.price || 0,
            sku: products[0]?.itemId
          }],
          listTotal,
          uniqueSkuCount,
          quantityTotal
        });
      } else if (products && isMultiAdd) {

        // 6.10.2 New List Created w/Product Add - multiple items from SelectList
        const multipleItemsForAnalytics = products.map((prod) => ({
          quantity: prod.quantity || 1,
          price: prod.price || 0,
          sku: prod.itemId
        }));

        LIFE_CYCLE_EVENT_BUS.trigger(TRACK.NEW_LIST_CREATED_WITH_PRODUCT_ADD, {
          location: creationLocation,
          items: multipleItemsForAnalytics,
          listTotal,
          uniqueSkuCount,
          quantityTotal
        });
      }
      onCreate();
    } else if (createListResponse?.error) {
      const gqlErrors = createListResponse?.error?.graphQLErrors;
      gqlErrors.forEach(function (err, index) {
        if (err?.message === ERROR.LIST_ALREADY_EXISTS) {
          setError(ERROR.LIST_ALREADY_EXISTS);
        } else {
          setError('Error creating list. Please try again.');
        }
      });
    }
  }, [createListResponse?.data, createListResponse?.error]);

  const newList = createListResponse?.data?.createList;

  useEffect(() => {
    if (newList?.id) {
      setFavoriteCount(favoriteCount + 1);
      setNewListDetails({
        listName: newList.name,
        listId: newList.id
      });
      if (hasGroupInfo) {
        // Skip group selection — submit directly to newly created list
        submitWithGroups(newList.id);
      } else {
        setScreen(LIST_SELECT_GROUP);
      }
    }
  }, [newList?.id]);

  useEffect(() => {
    if (name && !error) {
      setSelectedList(
        { listId: null, listName: null }
      );
    }
  }, [name, error]);

  return (
    <>
      <DrawerBody>
        {
          selectedList?.error && (
            <Alert status="error">{selectedList?.error}</Alert>
          )
        }
        {
          !isMultiAdd ? (
            <AddToListProductInfo
              identifiers={identifiers}
              primaryImage={primaryImage}
              intent={products[0]?.intent}
            />
          ) : (
            <BulkProductsSummary products={products} />
          )
        }
        <FormController className="sui-mb-4 sui-px-6 sui-pt-6">
          <div className="sui-pt-2">
            <TextField
              label="List Name"
              optional
              fullWidth
              placeholder="New List"
              testid="new-list-name"
              value={name}
              onChange={(evt) => {
                setName(evt.target.value);
                getValidationError(evt.target.value);
              }}
              status={error ? 'error' : null}
              statusMessage={error}
            />
          </div>
        </FormController>
        <div className="sui-pb-4 sui-px-6 sui-text-base">-or-</div>
        <FormController className="sui-px-6">
          <FormLabel id="list-names" className="sui-cursor-default sui-text-base">
            Add to An Existing List:
          </FormLabel>
          <div className="sui-pt-2">
            <RadioGroup
              aria-labelledby="list-names"
              name="list-names"
              data-testid="form-group"
              value={selectedList?.listId || ''}
            >
              {(listDetails || []).map((list) => {
                return (
                  <SelectionControlLabel
                    className="sui-break-all"
                    key={list.listId}
                    value={list.listId}
                    label={list.listName}
                    onChange={(evt) => { selectList(list); }}
                  >
                    <Radio />
                  </SelectionControlLabel>
                );
              })}
            </RadioGroup>
          </div>
        </FormController>
      </DrawerBody>
      <DrawerFooter>
        <ButtonGroup orientation="vertical">
          <Button
            fullWidth
            variant="primary"
            onClick={handleNextAction}
            type="submit"
            disabled={!selectedList?.listId && (!name || !!error)}
            data-testid="save-list"
          >
            Next
          </Button>
          <Button
            fullWidth
            variant="secondary"
            onClick={() => { onClose('close', { isUserInitiated: true }); }}
            data-testid="cancel-add-to-list"
          >
            Cancel
          </Button>
        </ButtonGroup>
      </DrawerFooter>
    </>
  );
};

SelectList.propTypes = {
  itemId: PropTypes.string,
  quantity: PropTypes.number,
  isSharedList: PropTypes.bool.isRequired,
  listDetails: PropTypes.arrayOf(PropTypes.shape({
    listId: PropTypes.string,
    listName: PropTypes.string
  })),
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  isExistingLists: PropTypes.bool.isRequired,
  products: PropTypes.arrayOf(PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    price: PropTypes.number
  })),
  setFavoriteCount: PropTypes.func.isRequired,
  favoriteCount: PropTypes.number,
  productValue: PropTypes.number,
  setNewListDetails: PropTypes.func,
  identifiers: PropTypes.shape({
    brandName: PropTypes.string,
    productLabel: PropTypes.string
  }),
  primaryImage: PropTypes.shape({
    url: PropTypes.string
  }),
  selectedList: PropTypes.shape({
    listId: PropTypes.string,
    listName: PropTypes.string,
    error: PropTypes.string
  }),
  setSelectedList: PropTypes.func
};

SelectList.defaultProps = {
  itemId: null,
  quantity: 1,
  listDetails: [],
  products: [],
  favoriteCount: null,
  productValue: 0,
  setNewListDetails: () => {},
  identifiers: {},
  primaryImage: {},
  selectedList: { listId: null, listName: null },
  setSelectedList: () => {}
};

SelectList.dataModel = extend(
  {
    updateList: params({
      id: string().isRequired(),
      operationName: customType('UpdateOperation').enum(['Update', 'RemoveItems', 'AddItems']),
      products: arrayOf(customType('ProductInput').shape({
        itemId: string(),
        quantity: number()
      }).isRequired()
      )
    }).mutation().shape({
      id: string()
    }),
    listOperations: params({
      operationName: customType('Operation').enum(['AddItems', 'Create'], 'AddItems'),
      id: string().isRequired(),
      groups: arrayOf(customType('GroupInput').shape({
        groupId: string(),
        groupName: string(),
        groupOrder: string(),
        products: arrayOf(customType('ProductInput').shape({
          itemId: string(),
          quantity: number(),
          comments: string(),
          intent: string(),
          intentId: string(),
          intentResolved: bool()
        }))
      }))
    }).mutation().shape({
      id: string(),
    })
  }
);
